import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import type { OidcClientCreated, OidcProviderConfiguration } from './api'

export function OidcClientCredentialsModal({
  client,
  onClose,
  onRetryProvider,
  provider,
  providerError = false,
  providerLoading = false,
}: {
  client: OidcClientCreated | null
  onClose: () => void
  onRetryProvider?: () => void
  provider: OidcProviderConfiguration | null
  providerError?: boolean
  providerLoading?: boolean
}) {
  const { t } = useTranslation('adminIdp')
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)
  const [copiedClientSecret, setCopiedClientSecret] = useState<string | null>(null)
  const [copyResult, setCopyResult] = useState<{
    clientSecret: string
    status: 'error' | 'success'
  } | null>(null)
  const packageCopied = copiedClientSecret !== null && client?.client_secret === copiedClientSecret
  const copyStatus =
    client && copyResult?.clientSecret === client.client_secret ? copyResult.status : null

  const copy = async (value: string, isPackage = false) => {
    try {
      await navigator.clipboard.writeText(value)
      if (client) setCopyResult({ clientSecret: client.client_secret, status: 'success' })
      if (isPackage && client) setCopiedClientSecret(client.client_secret)
    } catch {
      if (client) setCopyResult({ clientSecret: client.client_secret, status: 'error' })
    }
  }
  const close = () => {
    setCloseConfirmOpen(false)
    onClose()
  }
  const requestClose = () => {
    if (client && !packageCopied) {
      setCloseConfirmOpen(true)
      return
    }
    close()
  }
  const isMcpOAuth = client?.client_kind === 'mcp_oauth'
  const scope = client
    ? [
        ...(isMcpOAuth ? [] : ['openid']),
        ...client.client_scope_assignments.map((item) => item.scope_name),
      ].join(' ')
    : ''
  const integrationPackage =
    client && provider
      ? JSON.stringify(
          {
            ...(isMcpOAuth
              ? {
                  access_token_format: 'opaque',
                  resource: client.resource_uri,
                }
              : {
                  applications: client.applications.map((application) => ({
                    id: application.id,
                    name: application.name,
                  })),
                  jwks_uri: provider.jwks_uri,
                  prompt: 'none',
                  signing_algorithm: provider.signing_algorithm,
                  userinfo_endpoint: provider.userinfo_endpoint,
                }),
            authorization_endpoint: provider.authorization_endpoint,
            client_id: client.client_id,
            client_secret: client.client_secret,
            code_challenge_method: provider.code_challenge_method,
            discovery_url: isMcpOAuth ? provider.oauth_discovery_url : provider.discovery_url,
            grant_type: 'authorization_code',
            issuer: provider.issuer,
            redirect_uris: client.redirect_uris,
            response_type: provider.response_type,
            revocation_endpoint: provider.revocation_endpoint,
            scope,
            token_endpoint: provider.token_endpoint,
            token_endpoint_auth_method: client.token_endpoint_auth_method,
          },
          null,
          2,
        )
      : ''

  if (client && closeConfirmOpen) {
    return (
      <ConfirmationDialog
        cancelLabel={t('actions.continueSaving')}
        confirmLabel={t('actions.closeAnyway')}
        description={t('clients.credentials.closeConfirm')}
        onCancel={() => setCloseConfirmOpen(false)}
        onConfirm={close}
        open
        title={t('clients.credentials.closeConfirmTitle')}
      />
    )
  }

  return (
    <Modal
      className="max-w-[760px]"
      closeLabel={t('actions.cancel')}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button
            disabled={!integrationPackage}
            onClick={() => void copy(integrationPackage, true)}
            variant="secondary"
          >
            {t('clients.credentials.copyPackage')}
          </Button>
          <Button onClick={requestClose}>{t('actions.done')}</Button>
        </div>
      }
      onClose={requestClose}
      open={Boolean(client)}
      subtitle={t('clients.credentials.oneTimeHint')}
      title={t('clients.credentials.title')}
    >
      {client ? (
        <div className="grid max-h-[62vh] gap-4 overflow-y-auto pr-1">
          {providerError ? (
            <div
              className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-danger)] px-3 py-2 text-sm text-[var(--color-danger)]"
              role="alert"
            >
              <span>{t('clients.credentials.providerLoadFailed')}</span>
              {onRetryProvider ? (
                <Button onClick={onRetryProvider} size="sm" variant="secondary">
                  {t('clients.credentials.retryProvider')}
                </Button>
              ) : null}
            </div>
          ) : providerLoading && !provider ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('clients.credentials.providerLoading')}
            </p>
          ) : null}
          {copyStatus ? (
            <p
              className={
                copyStatus === 'error'
                  ? 'text-sm text-[var(--color-danger)]'
                  : 'text-sm text-[var(--color-success)]'
              }
              role="status"
            >
              {t(
                copyStatus === 'error'
                  ? 'clients.credentials.copyFailed'
                  : 'clients.credentials.copySuccess',
              )}
            </p>
          ) : null}
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('parameters.discoveryUrl')}
            monospace
            onCopy={() =>
              void copy(
                (isMcpOAuth ? provider?.oauth_discovery_url : provider?.discovery_url) ?? '',
              )
            }
            showLabel
            value={(isMcpOAuth ? provider?.oauth_discovery_url : provider?.discovery_url) ?? ''}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('parameters.issuer')}
            monospace
            onCopy={() => void copy(provider?.issuer ?? '')}
            showLabel
            value={provider?.issuer ?? ''}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('clients.fields.clientId')}
            monospace
            onCopy={() => void copy(client.client_id)}
            showLabel
            value={client.client_id}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('clients.fields.clientSecret')}
            monospace
            onCopy={() => void copy(client.client_secret)}
            showLabel
            value={client.client_secret}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('clients.fields.authMethod')}
            monospace
            onCopy={() => void copy(client.token_endpoint_auth_method)}
            showLabel
            value={client.token_endpoint_auth_method}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('clients.fields.scope')}
            monospace
            onCopy={() => void copy(scope)}
            showLabel
            value={scope}
          />
          {isMcpOAuth ? (
            <CopyableReadonlyField
              copyLabel={t('actions.copy')}
              label={t('clients.fields.resource')}
              monospace
              onCopy={() => void copy(client.resource_uri ?? '')}
              showLabel
              value={client.resource_uri ?? t('clients.credentials.notConfigured')}
            />
          ) : null}
          {isMcpOAuth ? null : (
            <CopyableReadonlyField
              copyLabel={t('actions.copy')}
              label={t('clients.fields.callbackUrls')}
              monospace
              onCopy={() => void copy(client.redirect_uris.join('\n'))}
              showLabel
              value={client.redirect_uris.join('\n') || t('clients.credentials.notConfigured')}
            />
          )}
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('clients.credentials.protocol')}
            monospace
            onCopy={() =>
              void copy(
                isMcpOAuth
                  ? `${provider?.response_type ?? 'code'} / opaque access token / PKCE ${provider?.code_challenge_method ?? 'S256'}`
                  : `${provider?.response_type ?? 'code'} / ${provider?.signing_algorithm ?? 'RS256'} / PKCE ${provider?.code_challenge_method ?? 'S256'}`,
              )
            }
            showLabel
            value={
              isMcpOAuth
                ? `${provider?.response_type ?? 'code'} / opaque access token / PKCE ${provider?.code_challenge_method ?? 'S256'}`
                : `${provider?.response_type ?? 'code'} / ${provider?.signing_algorithm ?? 'RS256'} / PKCE ${provider?.code_challenge_method ?? 'S256'}`
            }
          />
        </div>
      ) : null}
    </Modal>
  )
}
